import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Eye, GraduationCap, Share2, Users, BarChart3, Table2, ClipboardList, UserPlus, X, Loader2, Target, Download } from 'lucide-react'
import DashboardSidebar from '../components/DashboardSidebar'
import ShareModal from '../components/ShareModal'
import { courseApi, exportApi, type ClassInfo } from '../api/lessonBuilderApi'
import { COURSE_LEVELS, type CourseDoc } from '../types/CourseDoc'

const levelLabel = (lv?: string) => COURSE_LEVELS.find((x) => x.value === lv)?.label || ''

// Hub khoá học: danh sách khoá của giáo viên.
const MyCoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<CourseDoc[]>([])
  const [shared, setShared] = useState<CourseDoc[]>([])
  const [shareTarget, setShareTarget] = useState<CourseDoc | null>(null)
  const [assignTarget, setAssignTarget] = useState<CourseDoc | null>(null)
  const [loadError, setLoadError] = useState('')
  const navigate = useNavigate()

  const refresh = () => {
    setLoadError('')
    Promise.all([courseApi.list(), courseApi.sharedWithMe()])
      .then(([mine, sharedCourses]) => {
        setCourses(mine)
        setShared(sharedCourses)
      })
      .catch((e) => {
        setCourses([])
        setShared([])
        setLoadError((e as Error)?.message || 'Không tải được danh sách khoá học.')
      })
  }
  useEffect(refresh, [])

  const createCourse = async () => {
    // Không hỏi popup nữa: tạo khoá nháp với tên mặc định rồi mở trang soạn để GV đặt tên.
    // ID NGẮN (không dùng uuid dài) cho gọn URL sửa khoá.
    const rid = (typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID() : Math.random().toString(36).slice(2)).replace(/-/g, '').slice(0, 8)
    const doc: CourseDoc = {
      id: `course-${rid}`,
      slug: '',              // để trống → backend tự tạo slug (không dấu) từ TÊN khoá
      title: 'Khoá học mới',
      description: '',
      lessonIds: [],
      status: 'draft',
    }
    const saved = await courseApi.save(doc)
    navigate(`/khoa-hoc/sua/${saved.id}`)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Xoá khoá học này?')) return
    try {
      await courseApi.remove(id)
    } catch (e) {
      alert('Xoá thất bại trên máy chủ: ' + ((e as Error)?.message || e))
    }
    refresh()
  }

  const handleExportScorm = async (course: CourseDoc) => {
    try {
      const check = await exportApi.preflightCourse(course.id)
      if (!check.ok) {
        alert(`Chưa thể xuất SCORM:\n- ${check.errors.join('\n- ')}`)
        return
      }
      if (check.warnings.length) {
        const ok = confirm(`Có ${check.warnings.length} cảnh báo trước khi xuất SCORM:\n- ${check.warnings.join('\n- ')}\n\nVẫn tiếp tục xuất?`)
        if (!ok) return
      }
      await exportApi.scormCourse(course.id, course.title)
    } catch (e) {
      alert('Xuất SCORM chưa thành công: ' + ((e as Error)?.message || e))
    }
  }

  return (
    <div className="flex bg-[#F8FAFC]">
      <DashboardSidebar />
      <main className="flex-1 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-gray-900">Khoá học của tôi</h1>
            <p className="text-sm text-gray-500">Tạo khoá học online từ các bài giảng đã soạn</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/bai-tap')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50"
            >
              <ClipboardList size={18} /> Bài tập
            </button>
            <button
              onClick={() => navigate('/so-diem')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50"
            >
              <Table2 size={18} /> Sổ điểm
            </button>
            <button
              onClick={() => navigate('/can-thiep-hoc-tap')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-50 text-rose-700 text-sm font-bold hover:bg-rose-100"
            >
              <Target size={18} /> Can thiệp
            </button>
            <button
              onClick={createCourse}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700"
            >
              <Plus size={18} /> Tạo khoá học
            </button>
          </div>
        </div>

        {loadError && (
          <div className="mb-4 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            Không tải được khoá học của tôi. Vui lòng đăng nhập lại hoặc kiểm tra backend. Chi tiết: {loadError}
          </div>
        )}

        {!loadError && courses.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-300">
            <GraduationCap size={48} className="mb-3" />
            <p className="text-gray-400 font-medium">Chưa có khoá học nào.</p>
          </div>
        )}

        {!loadError && courses.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                <div className="h-28 bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  {c.coverImage ? (
                    <img src={c.coverImage} alt={c.title} className="w-full h-full object-cover" />
                  ) : (
                    <GraduationCap className="text-white/80" size={36} />
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                        c.status === 'published' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                      }`}
                    >
                      {c.status === 'published' ? 'Đã xuất bản' : 'Bản nháp'}
                    </span>
                    <span className="text-[11px] text-gray-400">{c.lessonIds.length} bài</span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">{c.title}</h3>
                  {(c.category || levelLabel(c.level)) && (
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      {c.category && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{c.category}</span>
                      )}
                      {levelLabel(c.level) && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">{levelLabel(c.level)}</span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mb-4 line-clamp-2 flex-1">{c.subtitle || c.description || '—'}</p>
                  <div className="flex items-center flex-wrap gap-1">
                    <button
                      onClick={() => navigate(`/khoa-hoc/sua/${c.id}`)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gray-50 text-gray-700 text-sm font-semibold hover:bg-gray-100"
                    >
                      <Pencil size={15} /> Sửa
                    </button>
                    {c.status === 'published' && (
                      <button
                        onClick={() => navigate(`/khoa-hoc/${c.slug}`)}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100"
                      >
                        <Eye size={15} /> Xem
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/khoa-hoc/thong-ke/${c.id}`)}
                      title="Thống kê khoá học"
                      className="px-2 py-2 rounded-lg text-gray-400 hover:bg-amber-50 hover:text-amber-600"
                    >
                      <BarChart3 size={15} />
                    </button>
                    <button
                      onClick={() => setAssignTarget(c)}
                      title="Giao khoá cho lớp"
                      className="px-2 py-2 rounded-lg text-gray-400 hover:bg-emerald-50 hover:text-emerald-600"
                    >
                      <UserPlus size={15} />
                    </button>
                    {/* Nút Chia sẻ khoá TẠM ẨN — người được chia sẻ chưa vào được khoá; dùng "Giao khoá cho lớp".
                    <button
                      onClick={() => setShareTarget(c)}
                      title="Chia sẻ"
                      className="px-2 py-2 rounded-lg text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                    >
                      <Share2 size={15} />
                    </button> */}
                    <button
                      onClick={() => handleExportScorm(c)}
                      title="Xuất SCORM 1.2"
                      className="px-2 py-2 rounded-lg text-gray-400 hover:bg-indigo-50 hover:text-indigo-600"
                    >
                      <Download size={15} />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      title="Xoá khoá học"
                      className="px-2 py-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Khoá học được chia sẻ với tôi */}
        {shared.length > 0 && (
          <div className="mt-10">
            <h2 className="flex items-center gap-2 text-lg font-black text-gray-900 mb-4">
              <Users size={18} className="text-blue-600" /> Được chia sẻ với tôi
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shared.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/khoa-hoc/${c.slug}`)}
                  className="bg-white rounded-2xl border border-blue-100 shadow-sm p-5 text-left hover:border-blue-300 hover:shadow-md transition-all"
                >
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                    Chia sẻ
                  </span>
                  <h3 className="text-base font-bold text-gray-900 mt-2 mb-1">{c.title}</h3>
                  <p className="text-xs text-gray-400 line-clamp-2">{c.description || '—'}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {shareTarget && (
        <ShareModal
          resourceType="course"
          resourceId={shareTarget.id}
          resourceTitle={shareTarget.title}
          onClose={() => setShareTarget(null)}
        />
      )}

      {assignTarget && (
        <AssignClassModal course={assignTarget} onClose={() => setAssignTarget(null)} />
      )}
    </div>
  )
}

// Modal: giao khoá cho 1 LỚP (ghi danh hàng loạt HS đã có tài khoản + thông báo)
const AssignClassModal: React.FC<{ course: CourseDoc; onClose: () => void }> = ({ course, onClose }) => {
  const [classes, setClasses] = useState<ClassInfo[] | null>(null)
  const [sel, setSel] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string>('')

  useEffect(() => {
    courseApi.myClasses().then((cs) => { setClasses(cs); if (cs[0]) setSel(cs[0].name) })
  }, [])

  const assign = async () => {
    if (!sel) return
    setBusy(true); setResult('')
    try {
      const r = await courseApi.assignClass(course.id, sel)
      setResult(`✅ Đã giao cho ${r.assigned} học viên.` + (r.skipped_no_account ? ` (${r.skipped_no_account} HS chưa có tài khoản — bỏ qua)` : ''))
    } catch {
      setResult('Giao lớp chưa thành công, thử lại.')
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2"><UserPlus size={20} className="text-emerald-600" /> Giao khoá cho lớp</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-3 truncate">Khoá: <strong className="text-gray-800">{course.title}</strong></p>
        {classes === null ? (
          <div className="py-8 text-center text-gray-300"><Loader2 className="inline animate-spin" /></div>
        ) : classes.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">Bạn chưa có lớp nào. Thêm học viên ở mục Quản lý học sinh trước.</p>
        ) : (
          <>
            <label className="text-xs font-bold text-gray-500 uppercase">Chọn lớp</label>
            <select value={sel} onChange={(e) => setSel(e.target.value)}
              className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-200 bg-white">
              {classes.map((c) => (
                <option key={c.name} value={c.name}>{c.name} — {c.linked}/{c.total} HS có tài khoản</option>
              ))}
            </select>
            <p className="text-[11px] text-gray-400 mt-1">Chỉ ghi danh được HS đã đăng nhập (có tài khoản). HS sẽ nhận thông báo.</p>
            {result && <p className="text-sm mt-3 text-slate-700">{result}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100">Đóng</button>
              <button onClick={assign} disabled={busy} className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50">
                {busy ? 'Đang giao…' : 'Giao cho lớp'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default MyCoursesPage
